import React from 'react'

import { Navigate, useLocation } from 'react-router-dom'
import { Auth } from './auth'
import { useInactivityLogout } from '@valiant/ui-shared'
import { LoadingPage, createAuthProvider, type UserUIRole, isUserUIRole } from '@valiant/ui-shared'

const decodeJwtRole = (token: string): { role: UserUIRole; userId: string } => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const role = payload.role

    // Validate that the role is allowed in user UI
    if (!isUserUIRole(role)) {
      throw new Error(
        `Access denied: Role "${role}" is not authorized to access the user interface. Only USER and GROUP_OWNER roles are permitted.`,
      )
    }

    return { role: role as UserUIRole, userId: payload.sub }
  } catch (error) {
    if (error instanceof Error && error.message.includes('Access denied')) {
      throw error
    }
    throw new Error('Invalid or missing role in token')
  }
}

const { AuthProvider: BaseAuthProvider, useAuth } = createAuthProvider<UserUIRole>({
  storageKey: 'auth-user',
  authInstance: new Auth(),
  useInactivityLogout,
  decodeToken: decodeJwtRole,
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  return <BaseAuthProvider>{children}</BaseAuthProvider>
}

export { useAuth }

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth()
  const location = useLocation()

  // Show loading state while auth is being restored from localStorage
  if (auth.isLoading) {
    return <LoadingPage />
  }

  if (!auth.auth) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return children
}
